/**
 * CheckConnectionStatusUseCase
 *
 * Use case for checking connection status using the new ocpi-overview endpoint.
 * Simplifies the previous multi-call approach to a single API call.
 */

import type { OcpiAdminRepository } from '../data/ocpi-admin-repository';
import type { ConnectionStatus } from '../types/connection-status-types';

export class CheckConnectionStatusUseCase {
  constructor(private readonly repository: OcpiAdminRepository) {}

  /**
   * Execute connection status check using the new ocpi-overview endpoint
   *
   * @param operatorId - Nayax operator ID
   * @param operatorName - Operator display name
   * @returns ConnectionStatus with cpoType, connections, and metadata
   */
  async execute(operatorId: number, operatorName: string): Promise<ConnectionStatus> {
    // Use new unified endpoint - single API call
    const overview = await this.repository.getOcpiOverview(operatorId);

    // If null, operator is not configured (404 from API)
    if (overview === null) {
      return {
        operatorId,
        operatorName,
        connections: [],
        hasConnections: false,
        lastChecked: new Date(),
        cpoType: null, // null signals onboarding needed
      };
    }

    // Operator is configured - return full status with connections from overview
    return {
      operatorId,
      operatorName,
      connections: overview.connections,
      hasConnections: overview.connections.length > 0,
      lastChecked: new Date(),
      cpoType: overview.cpoType,
      ocpiId: overview.ocpiId,
    };
  }
}
